// src/lib/notifications.js
// Tahap 9c — Logic trigger notifikasi
// Rollaco Cycling Academy
//
// CARA PAKAI:
//   import { triggerDailyNotifications, triggerBadgeNotification } from '../../lib/notifications';
//
// Fungsi di file ini TIDAK perlu dipanggil untuk:
//   - coach_note   → otomatis via DB trigger (trg_notify_coach_note)
//   - badge_earned → otomatis via DB trigger (trg_notify_badge_earned)
//
// Fungsi yang PERLU dipanggil manual dari frontend:
//   - triggerDailyNotifications()  → dari CoachDashboard & AdminDashboard saat mount
//   - dismissSessionReminder()     → dari SessionInput setelah berhasil simpan sesi

import { supabase } from './supabase';

// ─────────────────────────────────────────────────────────────
// 1. GENERATE DAILY NOTIFICATIONS
//    Panggil dari: CoachDashboard.js & AdminDashboard.js (useEffect mount)
//    Yang dihasilkan:
//      - session_reminder  → ke atlet yang belum input hari ini
//      - athlete_absent    → ke coach + orang tua atlet tsb
//      - schedule_reminder → ke semua atlet (H-1 jadwal besok)
//
//    Guard sessionStorage: hanya dipanggil SEKALI per hari per browser,
//    aman dari React Strict Mode yang mount useEffect 2x di development.
// ─────────────────────────────────────────────────────────────
export async function triggerDailyNotifications() {
  // Guard: hanya panggil sekali per hari per browser session
  const today = new Date().toISOString().slice(0, 10) // 'YYYY-MM-DD'
  const key = `notif_triggered_${today}`
  if (sessionStorage.getItem(key)) {
    console.log('[Notif] Daily trigger sudah dijalankan hari ini, skip.')
    return { success: true, count: 0 }
  }
  sessionStorage.setItem(key, '1')

  try {
    const { data, error } = await supabase.rpc('generate_daily_notifications');
    if (error) throw error;
    console.log(`[Notif] Daily trigger: ${data} notifikasi dibuat`);
    return { success: true, count: data };
  } catch (err) {
    // Kalau gagal, hapus flag supaya bisa retry
    sessionStorage.removeItem(key)
    console.error('[Notif] triggerDailyNotifications error:', err);
    return { success: false, error: err.message };
  }
}

// ─────────────────────────────────────────────────────────────
// 2. DISMISS SESSION REMINDER
//    Panggil dari: SessionInput.js setelah handleSubmit sukses
//    Fungsi: tandai-read notif session_reminder milik atlet ini
//    agar bell tidak terus menyala setelah atlet input sesi.
// ─────────────────────────────────────────────────────────────
export async function dismissSessionReminder(athleteProfileId) {
  if (!athleteProfileId) return;
  try {
    const today = new Date().toISOString().split('T')[0]; // 'YYYY-MM-DD'

    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('recipient_id', athleteProfileId)
      .eq('type', 'session_reminder')
      .gte('created_at', today + 'T00:00:00Z');

    if (error) throw error;
    console.log('[Notif] Session reminder dismissed untuk:', athleteProfileId);
  } catch (err) {
    console.error('[Notif] dismissSessionReminder error:', err);
  }
}

// ─────────────────────────────────────────────────────────────
// 3. MANUAL INSERT NOTIF (low-level helper)
//    Gunakan jika perlu buat notif custom yang tidak di-cover trigger DB.
//    Contoh: admin kirim pengumuman ke semua atlet.
// ─────────────────────────────────────────────────────────────
export async function createNotification({
  recipientId,
  type,
  title,
  message,
  athleteId = null,
}) {
  if (!recipientId || !type || !title || !message) {
    console.error('[Notif] createNotification: parameter tidak lengkap');
    return { success: false };
  }
  try {
    const { error } = await supabase.rpc('create_notification', {
      p_recipient_id: recipientId,
      p_type:         type,
      p_title:        title,
      p_message:      message,
      p_athlete_id:   athleteId,
    });
    if (error) throw error;
    return { success: true };
  } catch (err) {
    console.error('[Notif] createNotification error:', err);
    return { success: false, error: err.message };
  }
}

// ─────────────────────────────────────────────────────────────
// 4. FETCH NOTIF USER (helper opsional — NotificationBell sudah punya sendiri)
//    Gunakan jika ada halaman dedicated /notifications
// ─────────────────────────────────────────────────────────────
export async function fetchNotifications(userId, limit = 30) {
  if (!userId) return [];
  try {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('recipient_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error('[Notif] fetchNotifications error:', err);
    return [];
  }
}

// ─────────────────────────────────────────────────────────────
// 5. MARK ALL READ
//    Alternatif jika mau panggil dari luar NotificationBell
// ─────────────────────────────────────────────────────────────
export async function markAllRead(userId) {
  if (!userId) return;
  try {
    const { error } = await supabase.rpc('mark_all_notifications_read', {
      p_recipient_id: userId,
    });
    if (error) throw error;
  } catch (err) {
    console.error('[Notif] markAllRead error:', err);
  }
}